import { useState } from "react";
import { ReaderAiPromptComposer } from "@/components/ui/reader-ai/prompt-composer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToofiMark } from "@/components/ui/toofi-mark";
import { ToofiResumeCard } from "@/components/student/toofi/toofi-resume-card";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import type { ReaderAiModeSelection } from "@/components/ui/reader-ai/types";
import type { ToofiProductMode } from "@/types/agentic-chat";
import type { ToofiResumeTarget } from "@/types/student-toofi";
import { FileText } from "lucide-react";

type ReaderAiHomeStateProps = {
  surfaceKind?: "reader" | "workspace";
  variant?: "workspace-home" | "reader-home";
  subtitle?: string;
  promptPlaceholder?: string;
  defaultProductMode?: ToofiProductMode | null;
  resumeTarget?: ToofiResumeTarget | null;
  onResumeTarget?: () => void;
  onUseFileContext?: () => void;
  onStartTextContextCapture?: () => void;
  onStartVisualContextCapture?: () => void;
  onStartTemporaryChat?: () => void;
  onStartChatWithPrompt: (prompt: string, selection?: ReaderAiModeSelection | null) => void;
  showResumeCard?: boolean;
  showTemporaryAction?: boolean;
};

export function ReaderAiHomeState({
  surfaceKind = "reader",
  variant = surfaceKind === "workspace" ? "workspace-home" : "reader-home",
  subtitle,
  promptPlaceholder,
  defaultProductMode,
  resumeTarget,
  onResumeTarget,
  onUseFileContext,
  onStartTextContextCapture,
  onStartVisualContextCapture,
  onStartTemporaryChat,
  onStartChatWithPrompt,
  showResumeCard = true,
  showTemporaryAction = false,
}: ReaderAiHomeStateProps) {
  const [selectedMode, setSelectedMode] = useState<ToofiProductMode | null>(defaultProductMode ?? null);
  const isPhone = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const heroSubtitle =
    subtitle ??
    (surfaceKind === "workspace"
      ? "Ask about your study, plans, or what to do next."
      : "Ask about this file or what to study next.");

  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden">
      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto flex min-h-full w-full max-w-4xl flex-col justify-center px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-xl">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-[1.45rem] border border-white/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(241,245,249,0.86))] shadow-[0_18px_36px_rgba(148,163,184,0.12)]">
                <ToofiMark transparent className="h-11 w-11 [&_svg]:!h-full [&_svg]:!w-full" />
              </div>
              <h1 className={cn("mt-4 font-semibold tracking-[-0.03em] text-slate-900", isPhone ? "text-[1.45rem]" : "text-[1.95rem]")}>
                What do you want to study?
              </h1>
              <p className={cn("mt-2 max-w-xl text-slate-600", isPhone ? "text-[12px] leading-5" : "text-[13px] leading-6")}>
                {heroSubtitle}
              </p>
            </div>

            {showResumeCard && resumeTarget && onResumeTarget ? (
              <div className="mt-5">
                <ToofiResumeCard target={resumeTarget} onResume={onResumeTarget} compact />
              </div>
            ) : null}

            <div className={cn("mx-auto w-full", showResumeCard && resumeTarget && onResumeTarget ? "mt-4" : "mt-6")}>
              <ReaderAiPromptComposer
                variant="home"
                surfaceKind={surfaceKind}
                viewport={isPhone ? "phone" : isTablet ? "tablet" : "desktop"}
                productMode={selectedMode}
                onProductModeChange={setSelectedMode}
                placeholder={promptPlaceholder}
                onSubmit={onStartChatWithPrompt}
                onSelectTextContext={surfaceKind === "reader" ? onStartTextContextCapture : undefined}
                onUseVisualContext={surfaceKind === "reader" ? onStartVisualContextCapture : undefined}
                showModeMenu
                showMemoryControls={false}
              />
            </div>

            {variant === "reader-home" && surfaceKind === "reader" && onUseFileContext ? (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-slate-200/80 bg-white/88 px-4 text-[12px] font-semibold text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition-all duration-150 hover:bg-white active:scale-95"
                  onClick={onUseFileContext}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Use this file
                </button>
              </div>
            ) : null}

            {showTemporaryAction && onStartTemporaryChat && variant !== "workspace-home" ? (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-slate-200/80 bg-white/88 px-4 text-[12px] font-semibold text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition-all duration-150 hover:bg-white active:scale-95"
                  onClick={onStartTemporaryChat}
                >
                  Temporary chat
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
